<template>
  <div v-if="specialOffers.length" class="sidebar-block special-offers-block">
    <div class="special-offers-block-title">
      {{ $texts('specialOffersBlockTitle') }}
    </div>
    <div
      v-if="specialOffers.length === 1 && specialOffers[0]"
      class="single-offer-wrapper"
    >
      <span class="special-offers-title !leading-7">
        {{ specialOffers[0].title }}
        <span v-if="specialOffers[0].timePeriod" class="special-offers-period">
          {{
            formatDate(
              specialOffers[0].timePeriod.startDate,
              specialOffers[0].timePeriod.endDate,
            )
          }}
        </span>
      </span>
      <div
        class="special-offers-description single-offer"
        v-html="specialOffers[0].description"
      />
    </div>
    <template v-else>
      <Accordeon
        v-for="(offer, index) in specialOffers"
        :key="index"
        class="multiple-offers"
      >
        <template #title>
          <span>{{ offer.title }}</span>
          <div v-if="offer.timePeriod" class="special-offers-period">
            {{
              formatDate(offer.timePeriod.startDate, offer.timePeriod.endDate)
            }}
          </div>
        </template>
        <template #content>
          <div class="special-offers-description" v-html="offer.description" />
        </template>
      </Accordeon>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { VenueSpecialOfferFragment } from '#graphql-operations'

defineProps<{
  specialOffers: VenueSpecialOfferFragment[]
}>()

const { $texts } = useEasyTexts()
const language = useCurrentLanguage()

const formatOptions = computed<Intl.DateTimeFormatOptions>(() => {
  return language.value === 'en'
    ? { month: 'short', day: 'numeric', year: 'numeric' }
    : { day: '2-digit', month: '2-digit', year: 'numeric' }
})

function format(timestampString: string) {
  const timestamp = parseInt(timestampString)
  return new Date(timestamp * 1000).toLocaleDateString(
    language.value === 'en' ? 'en-US' : 'de-DE',
    formatOptions.value,
  )
}

function formatDate(startOfferDate: string, endOfferDate: string) {
  return `${format(startOfferDate)} - ${format(endOfferDate)}`
}
</script>

<style lang="postcss">
.sidebar-block.special-offers-block {
  background-color: #f5f5f5;
  border-top: none;
  border-bottom: none;
  padding: 15px 20px;
  border-radius: 4px;
  margin-bottom: 2rem;
  @media (prefers-color-scheme: dark) {
    @apply bg-highlighted;
  }

  @screen belowDesktop {
    margin-top: 2rem;
  }

  &.has-margin-top {
    margin-top: 1.25rem;
  }

  & .special-offers-block-title,
  & .special-offers-title {
    @apply font-bold leading-none text-scheme-invert;
    font-size: 1rem;
  }
  & .special-offers-block-title {
    @apply border-b border-solid border-border;
    padding-bottom: 0.75rem;
  }

  & .special-offers-title {
    @apply mt-2;
  }
  & .accordeon .accordeon-trigger .button,
  & .dropdown-content {
    @apply justify-start bg-transparent shadow-none;
  }

  & .accordeon .accordeon-trigger .button::after {
    @apply text-primary;
  }
  .multiple-offers .accordeon-trigger .button {
    @apply h-auto flex-wrap py-2;
  }
  & .special-offers-period {
    @apply block;
  }
  & .special-offers-period {
    @apply font-bold text-muted;
  }
  & .single-offer-wrapper {
    padding-top: 0.25rem;
    @screen belowDesktop {
      padding-top: 0.75rem;
      & .special-offers-description {
        padding-bottom: 0.75rem;
      }
    }

    & .special-offers-period {
      padding-top: 0.75rem;
    }
  }
  & .special-offers-description {
    font-size: 1rem;

    &.single-offer {
      @apply border-b border-solid border-border;
      padding-bottom: 0.25rem;
      padding-top: 0.25rem;
    }
  }
}
</style>
